import React, { useEffect, useState } from 'react'
import { makeStyles } from '@material-ui/core'
import { Typography } from 'foundations-library/components'
import { equalTo, required } from 'validation-rules'
import PasswordTextField from './PasswordTextField'
import NewPasswordTextField from './NewPasswordTextField'
import { getMuiTheme } from '../../utils'
import { TextFieldType } from 'foundations-library/types'

const jss = (contextTheme) => {
    return {
        formInputsContainer: ({ theme: optionsTheme }) => {
            const themingStyle = optionsTheme === 'mui' ? getMuiTheme() : {}
            return {
                ...themingStyle,
                marginBottom: '10px'

            }
        },
        inputContainer: {
            display: 'flex',
            flexDirection: 'column',
            gap: '20px',
            marginBottom: '15px'
        }
    }
}

const useStyles = makeStyles(jss)

type FieldName = 'current' | 'new' | 'confirm'

enum PasswordField {
    current = 'current',
    new = 'new',
    confirm = 'confirm'
}

interface PropsInterface {
    onChange(v: string): any,
    onChangeValues?(vals: Record<FieldName, string>): any,
    isValid?(isValid: boolean): any,
    className?: string,
    mode?: 'update' | 'create',
    theme?: 'foundations' | 'mui',
    disabled?: boolean,
    oldFieldProps?: TextFieldType,
    newPasswordFieldProps?: TextFieldType,
    confirmFieldProps?: TextFieldType
}

const createFieldsWithDefaultValues = (mode: 'update' | 'create', value: any): Record<FieldName, any> => {
    const data = {
        new: value,
        confirm: value
    }
    if (mode === 'update') {
        data.current = value
    }

    return data
}

/**
 *
 */
const PasswordManagementForm = ({
    onChange,
    onChangeValues,
    isValid,
    className = '',
    mode = 'update',
    theme = 'foundations',
    disabled = false,
    oldFieldProps = {},
    newPasswordFieldProps = {},
    confirmFieldProps = {}
}: PropsInterface): JSX.Element => {
    const styles = useStyles({ theme })
    const [isValidState, setIsValidState] = useState<Record<FieldName, boolean>>(createFieldsWithDefaultValues(mode, false))
    const [values, setValues] = useState<Record<FieldName, string>>(createFieldsWithDefaultValues(mode, ''))

    useEffect(() => {
        const allFieldsAreValid = Object.values(isValidState).every((x) => x === true) && Object.values(values).every((x) => x)
        isValid && isValid(allFieldsAreValid)
    }, [isValidState, values])

    useEffect(() => {
        onChangeValues && onChangeValues(values)
    }, [values])

    useEffect(() => {
        onChange && onChange(values.new)
    }, [values.new])

    const updateIsValidState = (type: FieldName) => (value) => setIsValidState((prev) => ({ ...prev, [type]: value }))
    const updateValue = (type: FieldName) => (value) => setValues((prev) => ({ ...prev, [type]: value }))

    return <div className={`${className} password-management-form`} data-testid='password-management'>
        <div className={styles.formInputsContainer}>
            <div className={styles.inputContainer}>
                {mode === 'update' && <PasswordTextField
                    label='Current password'
                    value={values.current}
                    onChange={updateValue(PasswordField.current)}
                    rules={[required()]}
                    isValid={updateIsValidState(PasswordField.current)}
                    disabled={disabled}
                    data-testid='old-password'
                    {...oldFieldProps}
                />}

                <NewPasswordTextField
                    value={values.new}
                    onChange={updateValue(PasswordField.new)}
                    isValid={updateIsValidState(PasswordField.new)}
                    data-testid='new-password'
                    disabled={disabled}
                    {...newPasswordFieldProps}
                    mode={mode}
                />
            </div>
            <PasswordTextField
                disabled={!isValidState.new || !values.new || disabled}
                label='Confirm new password'
                value={values.confirm}
                onChange={updateValue(PasswordField.confirm)}
                rules={[required(), equalTo(values.new, undefined, 'Passwords don\'t match')]}
                isValid={updateIsValidState(PasswordField.confirm)}
                data-testid='confirm-password'
                {...confirmFieldProps}
            />
        </div>
        <div data-testid='password-note'>
            <Typography tag='span' variant='subtitle3' color='neutral-700' className='password-management-bottom-note-bold'>Note:</Typography>
            &nbsp;
            <Typography tag='span' variant='subtitle3' color='neutral-500' remoteConfigID='password_management_bottom_note' />
        </div>
    </div>
}

export default PasswordManagementForm
